import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgModule,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FFacetInfoModule } from '@wipo/w-angular/internal';
import { WInputTextBase, getInputId } from '@wipo/w-angular/shared';

import { CommonModule } from '@angular/common';
import {
  CreateControlAccessProvider, WOption, WOptionOrString, WUniversalModule, document, propChanged
} from '@wipo/w-angular/shared';
import { FormsModule } from '@angular/forms';
import { WButtonModule } from '@wipo/w-angular/w-button';
import { WClickOutsideModule, WHideEmptyModule } from '@wipo/w-angular/directives';
import { WInputDropdownModule } from '@wipo/w-angular/w-input-dropdown';
import { takeUntil } from 'rxjs/operators';

const FILTER_NOOP = (value: string, options: WOptionOrString[]) => options;
const FILTER_DEFAULT = (value: string, options: WOptionOrString[]) => {
  return options.filter((o: WOptionOrString) => {
    const label = typeof o === 'string' ? o : (o.label || '');
    return label.toLowerCase().includes(value.toLowerCase());
  });
}

@Component({
  selector: 'w-input-autocomplete',
  templateUrl: './w-input-autocomplete.component.html',

  host: {
    class: 'b-input-autocomplete'
  },
  providers: [CreateControlAccessProvider(WInputAutocompleteComponent)],
  standalone: false
})
/*
@id w-input-autocomplete
@name w-input-autocomplete
@status release
@description based on input-text design, except drops prefix suffix and provides autocomplete suggestions
*/
export class WInputAutocompleteComponent extends WInputTextBase implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('buttons') buttons!: ElementRef;

  @Input() autoCompleteOptions?: WOptionOrString[]; // dynamic list of autocomplete options
  @Input() customDropdown: boolean = false; // use custom option list (only emits event on selection)
  @Input() customFilter?: (
    value: string,
    options: (WOption | string)[]) => (WOption | string)[]; // provide optional filter for autocomplete list
  @Output() wDropdownClick = new EventEmitter<WOption | string>(); // emits option on click on custom list item

  dropdownOptionList?: WOptionOrString[];
  get hasFocus(): boolean {
    return document.activeElement === this.i?.nativeElement;
  }
  get filter() {
    return this.customFilter || FILTER_DEFAULT;
  }

  textList?: string[];
  nativeAutocompleteActive: boolean = false;
  autocompleteActive: boolean = false;
  autoId = getInputId('auto');

  emptyButtons: boolean = false;

  // tslint:disable-next-line: variable-name
  constructor(protected _renderer: Renderer2, protected _elementRef: ElementRef) { super(); }

  override ngOnInit(): void {
    super.ngOnInit();
    this.updateTextList();
    this.wChange.pipe(takeUntil(this.destruction)).subscribe(() => {
      this.updateListAndCheckActive();
    });
  }
  ngAfterViewInit(): void {
    this.isEmptyElement(this.buttons.nativeElement)?.pipe(takeUntil(this.destruction)).subscribe(e => this.emptyButtons = e);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (propChanged(changes, 'autoCompleteOptions')) {
      this.updateTextList();
    }
  }
  @HostListener('document:click', ['$event.target'])
  onClick(targetElement: HTMLElement) {
    const clickedInside = this._elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.autocompleteActive = false;
    }
  }
  checkIfActive(): void {
    // any decision what to show and when and what value to fill-in taken by calling code
    const isMatch = (!this.value ||
      (!!this.textList && this.textList.some((t: string) => t.includes(this.value))));
    // custom one does not rely on match, as far as we have a list
    this.autocompleteActive = (this.textList?.length ?? 0) > 0;
    this.nativeAutocompleteActive = this.autocompleteActive && !this.customDropdown && isMatch;
  }
  onFocus(event: FocusEvent) {
    // this does not work ideally, will not get triggered if we click in already focused input
    setTimeout(() => {
      this.checkIfActive();
      this.wFocus.next(event);
    });
  }
  /** this deals both with custom all events) and browser one (only enter)  */
  keydown(event: KeyboardEvent) {
    // special treatment - arrows, if no key it is triggered from mouse selection from autocomplete
    const handleInParent = this.handleUpAndDown(event, this.dropdownOptionList?.length ?? 0);
    if (this.customDropdown && !event.key || handleInParent) {
      // handled in parent (common with other navigable dropdowns) or invalid entry
    } else if (!this.customDropdown && !event.key || event.key === 'Enter') {
      // ENTER selects the option (might be Enter key from browser autocomplete, without event.key set)
      this.textList = [];
      if (this.dropdownOptionList && this.activeIndex > -1) {
        this.i.nativeElement.blur();
        this.wDropdownClick.emit(this.dropdownOptionList[this.activeIndex]);
      }
      this.autocompleteActive = false;
    } else {
      this.autocompleteActive = true;
    }
  }
  private updateListAndCheckActive() {
    if (!this.value) {
      // always hide dropdown if we clear value
      this.autocompleteActive = false;
      this.nativeAutocompleteActive = false;
    } else if (this.autocompleteActive) {
      this.updateTextList();
      this.checkIfActive();
    }
  }
  lastValue?: any;
  updateTextList(): void {
    if (this.lastValue === this.value) {
      // no change, do not update
      return;
    }
    this.lastValue = this.value;
    // apply filters if any
    this.dropdownOptionList = Array.isArray(this.autoCompleteOptions) ?
      this.filter(this.value, this.autoCompleteOptions) : [];
    // convert
    this.textList = !Array.isArray(this.dropdownOptionList) ? [] :
      this.dropdownOptionList.map(o => (typeof o === 'string' ? o : (o.label || '')));
    this.activeIndex = this.activeIndex > this.textList.length - 1 ? -1 : this.activeIndex;
    if (this.hasFocus) { this.checkIfActive(); }
  }
  dropdownClick(value: WOptionOrString) {
    this.autocompleteActive = false;
    this.wDropdownClick.emit(value);
  }
  /** actions */
  actionKeyup(event: KeyboardEvent) {
    this.wKeyup.next(event);
  }
  actionChange() {
    this.wChange.next(this.value);
  }
  actionBlur(event: FocusEvent) {
    this.nativeAutocompleteActive = false;
    this.wBlur.next(event)
  }
  trackByValue(index: number, item: string): string {
    return `${index}-${item}`;
  }
}
@NgModule({
  imports: [CommonModule, FormsModule, WInputDropdownModule, WClickOutsideModule,
    WButtonModule, WHideEmptyModule, FFacetInfoModule, WUniversalModule
  ],
  exports: [WInputAutocompleteComponent],
  declarations: [WInputAutocompleteComponent]
})
export class WInputAutocompleteModule { }
